import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import { cog } from '@galo-monitor/icons'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { RoleActions } from '@galo-monitor/stores/role'
import { colors } from '@galo-monitor/styles'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import Icon from '@galo-monitor/web-components/Icon'
import FarmRolesModal from '@galo-monitor/web-containers/modals/company/FarmRolesModal'
import RoleModulesModal from '@galo-monitor/web-containers/modals/company/RoleModulesModal/RoleModulesModal'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal/ErrorModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const FarmRolesScreen = ({ slug }) => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { getActionsBySlug } = useRoutes()

  const userActions = useMemo(
    () => getActionsBySlug(slug),
    [getActionsBySlug, slug]
  )

  const { data: roles, mutate, isValidating } = useGaloMonitorApi('/companies/farms/roles')

  const columns = useMemo(
    () => [
      {
        title: t('name of {this}', { this: t('role', { howMany: 1 }), howMany: 1, gender: 'male' }),
        field: 'name'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-farm-role',
        Component: FarmRolesModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, role) => {
      createDialog({
        id: 'edit-farm-role',
        Component: FarmRolesModal,
        props: {
          role,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, role) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('role', { howMany: 1 }),
            name: role.name
          }),
          onConfirm: () => {
            dispatch(RoleActions.removeFarmRole(
              role,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('role', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'module-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('role', { howMany: 1 }))
                    })  }.`,
                    message: `${ t('you need detach all users to delete this role', {
                      this: t('role', { howMany: 1 }),
                      action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.edit) {
        datatableActions.push(
          rowData => ({
            icon: () => <Icon icon={ cog } color={ colors.darkGrey } />,
            tooltip: t('configure {this}', { this: t('permission', { howMany: 2 }) }),
            onClick: (_, role) => {
              createDialog({
                id: 'farm-roles',
                Component: RoleModulesModal,
                props: {
                  role,
                  onSuccess: mutate
                }
              })
            },
            iconButtonProps: {
              size: 'small',
              ...(!rowData.isEditable ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )

        datatableActions.push(
          rowData => ({
            icon: DataTableIcons.Edit,
            tooltip: t('datagrid body edit tooltip'),
            onClick: onEditClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small',
              ...(!rowData.isEditable ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      if (userActions.delete) {
        datatableActions.push(
          rowData => ({
            icon: DataTableIcons.Delete,
            tooltip: t('datagrid body delete tooltip'),
            onClick: onDeleteClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small',
              ...(!rowData.isEditable ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      return datatableActions
    },
    [createDialog, isValidating, mutate, onDeleteClick, onEditClick, t, userActions.delete, userActions.edit]
  )

  return (
    <DataTable
      loading={ isValidating }
      data={ roles }
      actions={ actions }
      columns={ columns }
      onAddClick={ userActions.write && onAddClick }
    />
  )
}

FarmRolesScreen.propTypes = {
  slug: PropTypes.string.isRequired
}

export default FarmRolesScreen
